import { findSceneNode, getActivePage, getAllSceneNodes, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { DocumentChange } from "@/document/model/documentChange";
import { expandChangedNodeIdsWithGeometryDrift } from "@/document/model/geometryDrift";
import type { SceneNode } from "@/document/model/nodes";
import { frameUsesPatternLayout } from "@/document/operations/patternLayout";
import { nodeAncestorChain } from "@/document/operations/nodeWorldTransform";
import type { Rect } from "@/lib/math/rect";
import type { DisplayItem, DisplayList } from "./displayList";
import { translateNumberVertices } from "./tessellation/geometryPool";
import {
  documentToDisplayListUncached,
  type DisplayListOptions,
} from "./tessellation/walk";
import { patchRenderTelemetry } from "../renderTelemetry";

export interface RetainedSceneStats {
  retessellatedNodeCount: number;
  reusedNodeCount: number;
  transformPatchedNodeCount: number;
}

/**
 * Renderer-owned retained display list.
 * Keyed by document id + revision + page, not document object identity.
 */
export class RetainedScene {
  private documentId?: string;
  private revision = -1;
  private activePageId?: string;
  private rootSignature = "";
  private items: DisplayItem[] = [];
  private itemsByNodeId = new Map<string, DisplayItem[]>();
  private nodeBounds = new Map<string, Rect>();
  private nodeRotations = new Map<string, number>();
  private pendingChange?: DocumentChange;
  private stats: RetainedSceneStats = {
    retessellatedNodeCount: 0,
    reusedNodeCount: 0,
    transformPatchedNodeCount: 0,
  };

  getStats(): RetainedSceneStats {
    return { ...this.stats };
  }

  clear(): void {
    this.documentId = undefined;
    this.revision = -1;
    this.activePageId = undefined;
    this.rootSignature = "";
    this.items = [];
    this.itemsByNodeId.clear();
    this.nodeBounds.clear();
    this.nodeRotations.clear();
    this.pendingChange = undefined;
    this.stats = { retessellatedNodeCount: 0, reusedNodeCount: 0, transformPatchedNodeCount: 0 };
  }

  noteDocumentChange(change: DocumentChange | undefined): void {
    this.pendingChange = change;
  }

  /**
   * Apply a pending document change before prepare.
   * Returns true when the retained list is already valid for the new revision.
   */
  consumeDocumentChange(document: CurvaDocument, activePageId?: string): boolean {
    const change = this.pendingChange?.document === document ? this.pendingChange : undefined;
    this.pendingChange = undefined;
    if (!change) {
      return false;
    }

    if (
      this.documentId !== change.previousDocument.id
      || this.revision !== change.previousDocument.revision
      || this.activePageId !== activePageId
      || document.id !== change.previousDocument.id
    ) {
      return false;
    }

    // Commands often list only the edited node; auto-layout settle also moves
    // nested children / hug ancestors. Expand so incremental reuse stays valid.
    const changedNodeIds = expandChangedNodeIdsWithGeometryDrift(
      change.previousDocument,
      document,
      change.changedNodeIds,
    );

    if (change.previewCompatible && this.tryTransformPatch(document, changedNodeIds)) {
      this.revision = document.revision;
      this.rootSignature = rootChildSignature(document, activePageId);
      patchRenderTelemetry({
        prepareCache: "retained-transform",
        retessellatedNodeCount: 0,
      });
      return true;
    }

    if (this.tryIncrementalRetessellate(document, changedNodeIds, { activePageId })) {
      this.revision = document.revision;
      this.rootSignature = rootChildSignature(document, activePageId);
      patchRenderTelemetry({ prepareCache: "retained-incremental" });
      return true;
    }

    return false;
  }

  ensureDisplayList(document: CurvaDocument, options: DisplayListOptions = {}): DisplayList {
    const hasPreviewOptions = hasDisplayListPreviewOptions(options);
    const rootSignature = rootChildSignature(document, options.activePageId);
    if (
      !hasPreviewOptions
      && this.documentId === document.id
      && this.revision === document.revision
      && this.activePageId === options.activePageId
      && this.rootSignature === rootSignature
      && this.items.length > 0
    ) {
      this.stats = { retessellatedNodeCount: 0, reusedNodeCount: this.itemsByNodeId.size, transformPatchedNodeCount: 0 };
      patchRenderTelemetry({ retessellatedNodeCount: 0 });
      return { items: this.items };
    }

    if (hasPreviewOptions && this.canPatchPreviews(document, options)) {
      return this.patchPreviews(document, options);
    }

    return this.rebuild(document, options);
  }

  /** Seed retained state from an already-built item list (e.g. preview promotion). */
  adoptDisplayList(
    document: CurvaDocument,
    displayList: DisplayList,
    activePageId?: string,
  ): void {
    this.documentId = document.id;
    this.revision = document.revision;
    this.activePageId = activePageId;
    this.rootSignature = rootChildSignature(document, activePageId);
    this.items = displayList.items;
    this.reindex(document);
    this.pendingChange = undefined;
  }

  private canPatchPreviews(document: CurvaDocument, options: DisplayListOptions): boolean {
    return (
      this.documentId === document.id
      && this.revision === document.revision
      && this.activePageId === options.activePageId
      && this.items.length > 0
      && !options.hiddenNodeIds?.size
    );
  }

  private patchPreviews(document: CurvaDocument, options: DisplayListOptions): DisplayList {
    const dirtyIds = new Set<string>([
      ...(options.vectorNetworkPreviews?.keys() ?? []),
      ...(options.booleanSourcePreviews?.keys() ?? []),
      ...(options.textPreviews?.keys() ?? []),
    ]);

    // Boolean previews may affect boolean nodes that reference sources.
    if (options.booleanSourcePreviews?.size) {
      for (const node of getAllSceneNodes(document)) {
        if (node.type === "boolean" && node.sourceNodes.some(source => options.booleanSourcePreviews!.has(source.id))) {
          dirtyIds.add(node.id);
        }
      }
    }

    if (dirtyIds.size === 0) {
      return { items: this.items };
    }

    const previewList = documentToDisplayListUncached(document, options);
    const replacement = new Map<string, DisplayItem[]>();
    for (const item of previewList.items) {
      if (!dirtyIds.has(item.id)) {
        continue;
      }
      const list = replacement.get(item.id) ?? [];
      list.push(item);
      replacement.set(item.id, list);
    }

    const nextItems: DisplayItem[] = [];
    const seen = new Set<string>();
    for (const item of this.items) {
      if (!dirtyIds.has(item.id)) {
        nextItems.push(item);
        continue;
      }
      if (seen.has(item.id)) {
        continue;
      }
      seen.add(item.id);
      nextItems.push(...(replacement.get(item.id) ?? []));
    }

    // Append any newly introduced dirty ids (should be rare for previews).
    for (const nodeId of dirtyIds) {
      if (!seen.has(nodeId) && replacement.has(nodeId)) {
        nextItems.push(...replacement.get(nodeId)!);
      }
    }

    this.stats = {
      retessellatedNodeCount: dirtyIds.size,
      reusedNodeCount: Math.max(0, this.itemsByNodeId.size - dirtyIds.size),
      transformPatchedNodeCount: 0,
    };
    patchRenderTelemetry({ retessellatedNodeCount: dirtyIds.size });

    // Preview patches are ephemeral — do not advance retained committed items.
    return { items: nextItems };
  }

  private tryTransformPatch(document: CurvaDocument, changedNodeIds: string[]): boolean {
    if (changedNodeIds.length === 0 || this.items.length === 0) {
      return false;
    }

    // Pattern copies are multiple display items under one child id, differentiated
    // by instance worldTransforms. A translation patch cannot reliably update them
    // (and promoting drag previews that remapped every copy onto the same bounds
    // leaves them collapsed). Force incremental retessellation instead.
    if (documentChangeInvolvesPatternLayout(document, changedNodeIds)) {
      return false;
    }

    // Container moves update descendant bounds in the document but often only
    // list the container in changedNodeIds. Expand so retained items for those
    // children (including pattern copies keyed by the same child id) translate too.
    const patchIds = expandTransformPatchNodeIds(document, changedNodeIds);

    // Baked display clips are ancestor intersections (A ∩ B ∩ …). Translating them
    // with a moved subtree is only valid when every clipping ancestor moves too —
    // otherwise a truncated A∩B stays truncated after B re-enters A (nested clip bug).
    if (transformPatchWouldCorruptClips(document, patchIds)) {
      return false;
    }

    // Clips are shared across siblings / pattern copies; mutate each once.
    const translatedClips = new Set<object>();

    for (const nodeId of patchIds) {
      const node = findSceneNode(document, nodeId);
      const previousBounds = this.nodeBounds.get(nodeId);
      const previousItems = this.itemsByNodeId.get(nodeId);
      if (!node || !previousBounds || !previousItems || previousItems.length === 0) {
        return false;
      }

      const widthChanged = Math.abs(node.bounds.width - previousBounds.width) > 0.001;
      const heightChanged = Math.abs(node.bounds.height - previousBounds.height) > 0.001;
      if (widthChanged || heightChanged) {
        return false;
      }

      // The transform patch only translates baked world-space geometry; a rotation
      // change (which is baked into the retained vertices) cannot be represented by a
      // translation, so bail and let incremental retessellation rebuild the node.
      const previousRotation = this.nodeRotations.get(nodeId) ?? 0;
      if (Math.abs((node.transform.rotation ?? 0) - previousRotation) > 0.0001) {
        return false;
      }

      const dx = node.bounds.x - previousBounds.x;
      const dy = node.bounds.y - previousBounds.y;
      if (Math.abs(dx) <= 0.0001 && Math.abs(dy) <= 0.0001) {
        this.nodeBounds.set(nodeId, { ...node.bounds });
        continue;
      }

      for (const item of previousItems) {
        item.bounds = { ...item.bounds, x: item.bounds.x + dx, y: item.bounds.y + dy };
        if (item.visualBounds) {
          item.visualBounds = {
            ...item.visualBounds,
            x: item.visualBounds.x + dx,
            y: item.visualBounds.y + dy,
          };
        }
        if (item.vertices.length > 0) {
          item.vertices = translateNumberVertices(item.vertices, dx, dy);
        }
        if (item.localVertices && item.localVertices.length > 0) {
          // local vertices stay local; world comes from transform/offset
        }
        if (item.positionOffset) {
          item.positionOffset = {
            x: item.positionOffset.x + dx,
            y: item.positionOffset.y + dy,
          };
        }
        if (item.clip && !translatedClips.has(item.clip)) {
          translatedClips.add(item.clip);
          item.clip.bounds = {
            ...item.clip.bounds,
            x: item.clip.bounds.x + dx,
            y: item.clip.bounds.y + dy,
          };
          item.clip.polygon = item.clip.polygon.map(point => ({
            x: point.x + dx,
            y: point.y + dy,
          }));
        }
        if (item.shadowContours) {
          item.shadowContours = item.shadowContours.map(contour =>
            contour.map(point => ({ x: point.x + dx, y: point.y + dy })),
          );
        }
        if (item.fill.type === "linearGradient") {
          item.fill = {
            ...item.fill,
            start: [item.fill.start[0] + dx, item.fill.start[1] + dy],
            end: [item.fill.end[0] + dx, item.fill.end[1] + dy],
          };
        } else if (item.fill.type === "radialGradient") {
          item.fill = {
            ...item.fill,
            center: [item.fill.center[0] + dx, item.fill.center[1] + dy],
          };
        } else if (item.fill.type === "image") {
          item.fill = {
            ...item.fill,
            imageRect: {
              ...item.fill.imageRect,
              x: item.fill.imageRect.x + dx,
              y: item.fill.imageRect.y + dy,
            },
          };
        }
      }

      this.nodeBounds.set(nodeId, { ...node.bounds });
    }

    this.stats = {
      retessellatedNodeCount: 0,
      reusedNodeCount: this.itemsByNodeId.size,
      transformPatchedNodeCount: patchIds.length,
    };
    return true;
  }

  private tryIncrementalRetessellate(
    document: CurvaDocument,
    changedNodeIds: string[],
    options: DisplayListOptions,
  ): boolean {
    if (changedNodeIds.length === 0 || this.items.length === 0) {
      return false;
    }

    const dirtyIds = new Set(changedNodeIds);
    // Structure changes (add/remove) cannot be patched from changed ids alone.
    const previousIds = new Set(this.itemsByNodeId.keys());
    const currentIds = new Set(getAllSceneNodes(document).map(node => node.id));
    for (const id of previousIds) {
      if (!currentIds.has(id)) {
        return false;
      }
    }
    for (const id of currentIds) {
      if (!previousIds.has(id)) {
        return false;
      }
    }

    const fresh = documentToDisplayListUncached(document, {
      ...options,
      dirtyNodeIds: dirtyIds,
      reuseItems: this.itemsByNodeId,
    });

    this.items = fresh.items;
    this.reindex(document);
    this.stats = {
      retessellatedNodeCount: dirtyIds.size,
      reusedNodeCount: Math.max(0, this.itemsByNodeId.size - dirtyIds.size),
      transformPatchedNodeCount: 0,
    };
    patchRenderTelemetry({ retessellatedNodeCount: dirtyIds.size });
    return true;
  }

  private rebuild(document: CurvaDocument, options: DisplayListOptions): DisplayList {
    const displayList = documentToDisplayListUncached(document, options);
    if (!hasDisplayListPreviewOptions(options)) {
      this.documentId = document.id;
      this.revision = document.revision;
      this.activePageId = options.activePageId;
      this.rootSignature = rootChildSignature(document, options.activePageId);
      this.items = displayList.items;
      this.reindex(document);
    }

    const nodeCount = new Set(displayList.items.map(item => item.id)).size;
    this.stats = {
      retessellatedNodeCount: nodeCount,
      reusedNodeCount: 0,
      transformPatchedNodeCount: 0,
    };
    patchRenderTelemetry({ retessellatedNodeCount: nodeCount });
    return displayList;
  }

  private reindex(document: CurvaDocument): void {
    this.itemsByNodeId.clear();
    this.nodeBounds.clear();
    this.nodeRotations.clear();
    for (const item of this.items) {
      const list = this.itemsByNodeId.get(item.id) ?? [];
      list.push(item);
      this.itemsByNodeId.set(item.id, list);
    }
    for (const node of getAllSceneNodes(document)) {
      if (this.itemsByNodeId.has(node.id)) {
        this.nodeBounds.set(node.id, { ...node.bounds });
        this.nodeRotations.set(node.id, node.transform.rotation ?? 0);
      }
    }
  }
}

export function hasDisplayListPreviewOptions(options: DisplayListOptions): boolean {
  return Boolean(
    (options.vectorNetworkPreviews && options.vectorNetworkPreviews.size > 0)
    || (options.booleanSourcePreviews && options.booleanSourcePreviews.size > 0)
    || (options.textPreviews && options.textPreviews.size > 0)
    || (options.hiddenNodeIds && options.hiddenNodeIds.size > 0),
  );
}

function rootChildSignature(document: CurvaDocument, _activePageId?: string): string {
  // Include the full page node set so distinct fixtures that share document id/revision
  // (common in unit tests) do not incorrectly reuse a retained list.
  return getAllSceneNodes(document).map(node => {
    const bounds = node.bounds;
    const rotation = node.transform.rotation ?? 0;
    return `${node.id}:${node.type}:${bounds.x},${bounds.y},${bounds.width},${bounds.height}:${rotation}:${node.visible ? 1 : 0}${nodeGeometrySignature(node)}`;
  }).join("|");
}

function expandTransformPatchNodeIds(document: CurvaDocument, changedNodeIds: string[]): string[] {
  const ids = new Set<string>();
  const visit = (node: SceneNode): void => {
    ids.add(node.id);
    const children = getSceneNodeChildren(node);
    if (!children) {
      return;
    }
    for (const child of children) {
      visit(child);
    }
  };

  for (const nodeId of changedNodeIds) {
    const node = findSceneNode(document, nodeId);
    if (node) {
      visit(node);
    } else {
      ids.add(nodeId);
    }
  }
  return [...ids];
}

export function documentChangeInvolvesPatternLayout(document: CurvaDocument, changedNodeIds: string[]): boolean {
  for (const nodeId of changedNodeIds) {
    const node = findSceneNode(document, nodeId);
    if (!node) {
      continue;
    }
    if (node.type === "frame" && frameUsesPatternLayout(node)) {
      return true;
    }
    for (const ancestor of nodeAncestorChain(document, nodeId)) {
      if (ancestor.type === "frame" && frameUsesPatternLayout(ancestor)) {
        return true;
      }
    }
    for (const descendantId of expandTransformPatchNodeIds(document, [nodeId])) {
      if (descendantId === nodeId) {
        continue;
      }
      const descendant = findSceneNode(document, descendantId);
      if (descendant?.type === "frame" && frameUsesPatternLayout(descendant)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * True when translating baked display clips with `patchIds` would leave a stale
 * intersection: some clipping ancestor stays fixed in world space while the
 * patched subtree moves (classic nested clipsContent frame move).
 */
export function transformPatchWouldCorruptClips(document: CurvaDocument, patchIds: string[]): boolean {
  const ids = new Set(patchIds);
  for (const nodeId of patchIds) {
    for (const ancestor of nodeAncestorChain(document, nodeId)) {
      if (ancestor.type === "frame" && ancestor.clipsContent && !ids.has(ancestor.id)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Extra per-node geometry fingerprint for node types whose rendered geometry can
 * change without the axis-aligned bounds changing — most notably vector networks
 * during sub-element (vertex/handle) animation, where a vertex can move inside an
 * already-established bounding box. Without this, the retained list would keep
 * serving stale tessellation for the animated node.
 */
function nodeGeometrySignature(node: SceneNode): string {
  if (node.type !== "vectorNetwork") {
    return "";
  }
  const network = node.network;
  let checksum = 0;
  let k = 1;
  for (const vertex of network.vertices) {
    checksum += vertex.x * k + vertex.y * (k + 1);
    k += 2;
  }
  for (const edge of network.edges) {
    if (edge.from.handle) {
      checksum += edge.from.handle.x * k + edge.from.handle.y * (k + 1);
    }
    k += 2;
    if (edge.to.handle) {
      checksum += edge.to.handle.x * k + edge.to.handle.y * (k + 1);
    }
    k += 2;
  }
  return `:${network.vertices.length}/${network.edges.length}/${checksum.toFixed(3)}`;
}

/** Module-level retained scene for export/tests that call documentToDisplayList directly. */
let sharedRetainedScene = new RetainedScene();

export function getSharedRetainedScene(): RetainedScene {
  return sharedRetainedScene;
}

export function resetSharedRetainedSceneForTests(): RetainedScene {
  sharedRetainedScene = new RetainedScene();
  return sharedRetainedScene;
}

export function activePageChildrenLength(document: CurvaDocument, activePageId?: string): number {
  return getActivePage(document, activePageId)?.children.length ?? 0;
}

/** Public entry used by renderer, export, and tests — backed by the shared retained scene. */
export function documentToDisplayList(document: CurvaDocument, options: DisplayListOptions = {}): DisplayList {
  return getSharedRetainedScene().ensureDisplayList(document, options);
}
